/**
 * SB6-ANA-CACHE-03 — Storage section for the Settings modal.
 *
 * Slots into the SETTINGS-01 modal as a new "Storage" category (left-rail tab).
 * It surfaces and controls the image cache the rest of CACHE-01/02/03 builds:
 *
 *  - **Total usage** via `navigator.storage.estimate()` + the persisted state
 *    from `navigator.storage.persisted()` (granted/denied/unsupported).
 *  - **Per-dataset rows** — name, size, completeness ("312/369"), last used,
 *    **pin toggle**, **clear** (AC4).
 *  - **Clear all image cache** — empties the `wp-images-v1` bucket + every
 *    manifest record; the open dataset keeps working (re-fetches) (AC5).
 *  - **Two budget settings** — max GB and max unpinned datasets (both
 *    user-configurable; defaults 10 GB / 3). Persisted client-side
 *    (`storageBudget`, localStorage — no wire-shape change).
 *
 * Everything reads/writes the same manifest + budget stores the evictor uses, so
 * the UI is always truthful (a clear/pin/evict re-renders these rows live).
 */
import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCacheManifest } from "../cache/useCacheManifest.js";
import { useStorageBudget } from "../cache/useStorageBudget.js";
import { usePersistenceState } from "../cache/usePersistenceState.js";
import { storageBudget } from "../cache/storageBudget.js";
import {
  clearAllImageCache,
  clearDataset,
  readStorageEstimate,
  setPinned,
  type StorageEstimateResult,
} from "../cache/persistence.js";
import { getOpenAcquisitionId } from "../cache/manifestBridge.js";
import { formatBytes, bytesToGb, gbToBytes } from "../cache/formatBytes.js";
import { PinIcon, Trash2Icon } from "@aeriosiq/ui/icons";

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      component="h3"
      variant="subtitle2"
      sx={{
        textTransform: "uppercase",
        letterSpacing: 0.5,
        color: "text.secondary",
        fontWeight: 600,
        mt: 2,
        mb: 0.5,
      }}
    >
      {children}
    </Typography>
  );
}

function relativeTime(epochMs: number): string {
  const diff = Date.now() - epochMs;
  if (diff < 60_000) return "just now";
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${String(mins)} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${String(hours)} h ago`;
  const days = Math.floor(hours / 24);
  return `${String(days)} d ago`;
}

export function StorageSettings(): React.ReactElement {
  const manifests = useCacheManifest();
  const budget = useStorageBudget();
  const persist = usePersistenceState();

  const [estimate, setEstimate] = useState<StorageEstimateResult>({
    usage: null,
    quota: null,
  });
  // Local input state for the two budget fields (committed on blur/Enter) so
  // typing mid-number doesn't snap to a clamped value on each keystroke.
  const [gbInput, setGbInput] = useState(() =>
    String(Math.round(bytesToGb(budget.maxBytes) * 100) / 100),
  );
  const [countInput, setCountInput] = useState(() =>
    String(budget.maxUnpinnedDatasets),
  );

  // Re-read the live estimate whenever the manifest changes (a clear/evict
  // shrinks usage; a download grows it).
  useEffect(() => {
    let cancelled = false;
    void readStorageEstimate().then((est) => {
      if (!cancelled) setEstimate(est);
    });
    return () => {
      cancelled = true;
    };
  }, [manifests]);

  // Keep the inputs in sync if the budget changes elsewhere.
  useEffect(() => {
    setGbInput(String(Math.round(bytesToGb(budget.maxBytes) * 100) / 100));
  }, [budget.maxBytes]);
  useEffect(() => {
    setCountInput(String(budget.maxUnpinnedDatasets));
  }, [budget.maxUnpinnedDatasets]);

  const openId = getOpenAcquisitionId();

  const commitGb = (): void => {
    const gb = Number.parseFloat(gbInput);
    if (Number.isFinite(gb) && gb > 0) storageBudget.setMaxBytes(gbToBytes(gb));
  };
  const commitCount = (): void => {
    const n = Number.parseInt(countInput, 10);
    if (Number.isFinite(n) && n >= 1) storageBudget.setMaxUnpinnedDatasets(n);
  };

  const totalCachedBytes = manifests.reduce((sum, m) => sum + m.totalBytes, 0);
  const usagePct =
    estimate.usage != null && estimate.quota != null && estimate.quota > 0
      ? Math.min(100, (estimate.usage / estimate.quota) * 100)
      : null;

  const persistLabel: Record<typeof persist, string> = {
    granted: "Persistent — exempt from browser cleanup",
    denied: "Not persistent — browser may evict under disk pressure",
    unknown: "Persistence not yet requested",
    unsupported: "Persistence not supported by this browser",
  };

  return (
    <Box data-testid="storage-settings">
      <SectionHeading>Total usage</SectionHeading>
      <Stack spacing={0.75} sx={{ mb: 1 }}>
        <Typography variant="body2" data-testid="storage-total-usage">
          {estimate.usage != null
            ? `${formatBytes(estimate.usage)} used${
                estimate.quota != null
                  ? ` of ${formatBytes(estimate.quota)} available`
                  : ""
              }`
            : `${formatBytes(totalCachedBytes)} of image cache (origin total unavailable)`}
        </Typography>
        {usagePct != null ? (
          <LinearProgress
            variant="determinate"
            value={usagePct}
            sx={{ maxWidth: 460, borderRadius: 1 }}
            aria-label="Origin storage usage"
          />
        ) : null}
        <Chip
          size="small"
          variant="outlined"
          color={
            persist === "granted"
              ? "success"
              : persist === "denied"
                ? "warning"
                : "default"
          }
          label={persistLabel[persist]}
          data-testid="storage-persist-state"
          sx={{ alignSelf: "flex-start" }}
        />
      </Stack>

      <SectionHeading>Cached datasets</SectionHeading>
      {manifests.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="storage-no-datasets"
        >
          No datasets cached yet. Open a dataset and use “Download dataset” to
          warm its full-resolution images for offline-fast navigation.
        </Typography>
      ) : (
        <Stack
          divider={<Divider flexItem />}
          data-testid="storage-dataset-list"
          sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}
        >
          {manifests
            .slice()
            .sort((a, b) => b.lastAccess - a.lastAccess)
            .map((m) => {
              const complete =
                m.totalImageCount > 0
                  ? `${String(m.imageKeys.length)}/${String(m.totalImageCount)}`
                  : String(m.imageKeys.length);
              const isOpen = m.dataAcquisitionId === openId;
              return (
                <Stack
                  key={m.dataAcquisitionId}
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ px: 1.5, py: 1 }}
                  data-testid={`storage-row-${m.dataAcquisitionId}`}
                >
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{ fontWeight: 600 }}
                      title={m.name ?? m.dataAcquisitionId}
                    >
                      {m.name ?? m.dataAcquisitionId}
                      {isOpen ? (
                        <Chip
                          size="small"
                          label="Open"
                          sx={{ ml: 1, height: 18 }}
                        />
                      ) : null}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <Box
                        component="span"
                        data-testid={`storage-row-size-${m.dataAcquisitionId}`}
                      >
                        {formatBytes(m.totalBytes)}
                      </Box>
                      {" · "}
                      <Box
                        component="span"
                        data-testid={`storage-row-complete-${m.dataAcquisitionId}`}
                      >
                        {complete} images
                      </Box>
                      {" · "}
                      {relativeTime(m.lastAccess)}
                    </Typography>
                  </Box>
                  <Tooltip
                    title={
                      m.pinned
                        ? "Pinned — exempt from automatic cleanup. Click to unpin."
                        : "Pin — keep across sessions, exempt from cleanup."
                    }
                  >
                    <IconButton
                      size="small"
                      onClick={() => {
                        void setPinned(m.dataAcquisitionId, !m.pinned);
                      }}
                      aria-label={
                        m.pinned
                          ? `Unpin ${m.name ?? m.dataAcquisitionId}`
                          : `Pin ${m.name ?? m.dataAcquisitionId}`
                      }
                      aria-pressed={m.pinned}
                      color={m.pinned ? "primary" : "default"}
                      data-testid={`storage-pin-${m.dataAcquisitionId}`}
                    >
                      {/* Filled when pinned — see AssetInfoBurgerMenu. The
                          button already carries `aria-pressed` and a colour,
                          so this is the third signal, not the only one. */}
                      <PinIcon fontSize="small" filled={m.pinned} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Clear this dataset from the cache">
                    <IconButton
                      size="small"
                      onClick={() => {
                        void clearDataset(m.dataAcquisitionId);
                      }}
                      aria-label={`Clear ${m.name ?? m.dataAcquisitionId}`}
                      data-testid={`storage-clear-${m.dataAcquisitionId}`}
                    >
                      <Trash2Icon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              );
            })}
        </Stack>
      )}
      {manifests.length > 0 ? (
        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            color="error"
            variant="text"
            startIcon={<Trash2Icon fontSize="small" />}
            onClick={() => {
              void clearAllImageCache();
            }}
            data-testid="storage-clear-all"
          >
            Clear all image cache
          </Button>
        </Box>
      ) : null}

      <SectionHeading>Cache budget</SectionHeading>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Unpinned datasets are cleaned up automatically (least-recently-used
        first) once either limit is exceeded. Pinned datasets are never cleaned
        up.
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          size="small"
          type="number"
          label="Max cache size"
          value={gbInput}
          onChange={(e) => {
            setGbInput(e.target.value);
          }}
          onBlur={commitGb}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitGb();
          }}
          slotProps={{
            input: {
              endAdornment: <InputAdornment position="end">GB</InputAdornment>,
            },
            htmlInput: {
              min: 1,
              step: 1,
              "aria-label": "Max cache size in GB",
            },
          }}
          data-testid="storage-budget-gb"
          sx={{ width: 200 }}
        />
        <TextField
          size="small"
          type="number"
          label="Max unpinned datasets"
          value={countInput}
          onChange={(e) => {
            setCountInput(e.target.value);
          }}
          onBlur={commitCount}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitCount();
          }}
          slotProps={{
            htmlInput: {
              min: 1,
              step: 1,
              "aria-label": "Max number of unpinned datasets",
            },
          }}
          data-testid="storage-budget-count"
          sx={{ width: 220 }}
        />
      </Stack>
      {persist === "denied" ? (
        <Alert severity="info" sx={{ mt: 2 }} icon={false}>
          The browser declined persistent storage, so the cache may be cleared
          under disk pressure. Pinning a dataset re-requests persistence.
        </Alert>
      ) : null}
    </Box>
  );
}
